import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CalendarDays,
  Check,
  Clock3,
  Edit3,
  Eye,
  EyeOff,
  MapPin,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";


const EMPTY_FORM = {
  titulo: "",
  categoria: "evento",
  tipo_local: "hub",
  modalidade: "coletiva",

  data_inicio: "",
  data_fim: "",

  hora_inicio: "",
  hora_fim: "",
  horario_a_definir: false,

  local: "",
  municipio: "",

  espaco_hub: "a_definir",
  espaco_hub_outro: "",

  responsavel: "",
  programa: "Hub Acre",
  descricao: "",

  status: "programada",
  publicado: true,
};


const CATEGORY_LABELS = {
  evento: "Evento",
  workshop: "Workshop",
  oficina: "Oficina",
  reuniao: "Reunião",
  representacao: "Representação institucional",
  consultoria: "Consultoria",
  atendimento: "Atendimento",
  formacao: "Formação",
  outro: "Outro",
};


const LOCATION_LABELS = {
  hub: "Interna no HUB",
  externa: "Externa com apoio do HUB",
};


const HUB_SPACE_LABELS = {
  a_definir: "Espaço a definir",
  sala_reuniao: "Sala de reunião",
  palco: "Palco",
  mesas: "Mesas",
  outro: "Outro",
};


const STATUS_LABELS = {
  programada: "Programada",
  confirmada: "Confirmada",
  realizada: "Realizada",
  cancelada: "Cancelada",
};


function brDate(value) {
  if (!value) {
    return "";
  }

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
}


function dateRange(item) {
  if (
    item.data_fim &&
    item.data_fim !== item.data_inicio
  ) {
    return (
      `${brDate(item.data_inicio)} a ` +
      brDate(item.data_fim)
    );
  }

  return brDate(item.data_inicio);
}


function timeRange(item) {
  if (item.horario_a_definir) {
    return "Horário a definir";
  }

  if (
    item.hora_inicio &&
    item.hora_fim
  ) {
    return `${item.hora_inicio} – ${item.hora_fim}`;
  }

  return (
    item.hora_inicio ||
    item.hora_fim ||
    "Horário não informado"
  );
}


async function agendaApi(
  path,
  options = {}
) {
  const response = await fetch(
    path,
    {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    }
  );

  let data = {};

  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    throw new Error(
      data.detail ||
      "Não foi possível concluir a operação."
    );
  }

  return data;
}


function AgendaAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [filterCategory, setFilterCategory] =
    useState("");

  const [filterLocation, setFilterLocation] =
    useState("");


  const loadItems = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const data = await agendaApi(
          "/api/admin/agenda"
        );

        setItems(
          data.itens || []
        );

      } catch (err) {
        setError(err.message);

      } finally {
        setLoading(false);
      }
    },
    []
  );


  useEffect(() => {
    loadItems();
  }, [loadItems]);


  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (
          filterCategory &&
          item.categoria !== filterCategory
        ) {
          return false;
        }

        if (
          filterLocation &&
          item.tipo_local !== filterLocation
        ) {
          return false;
        }

        return true;
      }),
    [
      items,
      filterCategory,
      filterLocation,
    ]
  );


  function change(
    field,
    value
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }


  function openNew() {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
    });
    setError("");
    setMessage("");
    setFormOpen(true);
  }


  function openEdit(item) {
    setEditing(item);

    setForm({
      titulo: item.titulo || "",
      categoria: item.categoria || "evento",
      tipo_local: item.tipo_local || "hub",
      modalidade: item.modalidade || "coletiva",

      data_inicio: item.data_inicio || "",
      data_fim: item.data_fim || "",

      hora_inicio: item.hora_inicio || "",
      hora_fim: item.hora_fim || "",

      horario_a_definir:
        Boolean(item.horario_a_definir),

      local: item.local || "",
      municipio: item.municipio || "",

      espaco_hub:
        item.espaco_hub || "a_definir",
      espaco_hub_outro:
        item.espaco_hub_outro || "",

      responsavel: item.responsavel || "",
      programa: item.programa || "",
      descricao: item.descricao || "",

      status: item.status || "programada",
      publicado: Boolean(item.publicado),
    });

    setError("");
    setMessage("");
    setFormOpen(true);
  }


  function closeForm() {
    if (saving) {
      return;
    }

    setFormOpen(false);
    setEditing(null);
  }


  async function save(event) {
    event.preventDefault();

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        ...form,

        data_fim:
          form.data_fim || null,

        hora_inicio:
          form.horario_a_definir
            ? null
            : form.hora_inicio || null,

        hora_fim:
          form.horario_a_definir
            ? null
            : form.hora_fim || null,
      };

      if (editing) {
        await agendaApi(
          `/api/admin/agenda/${editing.id}`,
          {
            method: "PUT",
            body: JSON.stringify(payload),
          }
        );

        setMessage(
          "Atividade atualizada com sucesso."
        );

      } else {
        await agendaApi(
          "/api/admin/agenda",
          {
            method: "POST",
            body: JSON.stringify(payload),
          }
        );

        setMessage(
          "Atividade cadastrada com sucesso."
        );
      }

      setFormOpen(false);
      setEditing(null);

      await loadItems();

    } catch (err) {
      setError(err.message);

    } finally {
      setSaving(false);
    }
  }


  async function remove(item) {
    const confirmed = window.confirm(
      `Excluir "${item.titulo}" da agenda?`
    );

    if (!confirmed) {
      return;
    }

    setError("");
    setMessage("");

    try {
      await agendaApi(
        `/api/admin/agenda/${item.id}`,
        {
          method: "DELETE",
        }
      );

      setMessage(
        "Atividade excluída."
      );

      await loadItems();

    } catch (err) {
      setError(err.message);
    }
  }


  async function togglePublished(item) {
    setError("");
    setMessage("");

    try {
      await agendaApi(
        `/api/admin/agenda/${item.id}/publicacao`,
        {
          method: "PATCH",
        }
      );

      await loadItems();

    } catch (err) {
      setError(err.message);
    }
  }


  return (
    <div className="admin-content agenda-admin">

      <div className="admin-page-heading agenda-heading">
        <div>
          <span className="admin-overline">
            HUB ACRE
          </span>

          <h1>
            Agenda
            <br />
            <strong>institucional.</strong>
          </h1>

          <p>
            Organize atividades, eventos,
            representações, oficinas e compromissos
            do HUB Acre.
          </p>
        </div>

        <div className="admin-heading-actions">
          <button
            className="admin-primary-button"
            onClick={openNew}
          >
            <Plus />
            Nova atividade
          </button>

          <button
            className="admin-icon-button"
            onClick={loadItems}
            title="Atualizar"
          >
            <RefreshCw />
          </button>
        </div>
      </div>


      {message && (
        <div className="agenda-message agenda-success">
          <Check />
          {message}
        </div>
      )}

      {error && (
        <div className="agenda-message agenda-error">
          {error}
        </div>
      )}


      <section className="agenda-summary">
        <div>
          <CalendarDays />
          <span>
            <strong>{items.length}</strong>
            atividades cadastradas
          </span>
        </div>

        <div>
          <Eye />
          <span>
            <strong>
              {
                items.filter(
                  (item) => item.publicado
                ).length
              }
            </strong>
            publicadas
          </span>
        </div>
      </section>


      <section className="admin-filter-card agenda-filters">
        <div>
          <select
            value={filterCategory}
            onChange={(event) =>
              setFilterCategory(
                event.target.value
              )
            }
          >
            <option value="">
              Todas as categorias
            </option>

            {Object.entries(
              CATEGORY_LABELS
            ).map(
              ([value, label]) => (
                <option
                  value={value}
                  key={value}
                >
                  {label}
                </option>
              )
            )}
          </select>

          <select
            value={filterLocation}
            onChange={(event) =>
              setFilterLocation(
                event.target.value
              )
            }
          >
            <option value="">
              Todos os locais
            </option>

            <option value="hub">
              Internas no HUB
            </option>

            <option value="externa">
              Externas com apoio do HUB
            </option>
          </select>
        </div>
      </section>


      {loading ? (
        <div className="admin-loading-card">
          <RefreshCw className="admin-spin" />
          Carregando agenda...
        </div>
      ) : (
        <section className="agenda-list">

          {!filteredItems.length && (
            <div className="agenda-empty">
              <CalendarDays />

              <strong>
                Nenhuma atividade encontrada
              </strong>

              <span>
                Cadastre uma atividade ou altere
                os filtros.
              </span>
            </div>
          )}

          {filteredItems.map((item) => (
            <article
              className={
                `agenda-card ${
                  !item.publicado
                    ? "agenda-card-hidden"
                    : ""
                }`
              }
              key={item.id}
            >
              <div className="agenda-card-date">
                <CalendarDays />

                <strong>
                  {dateRange(item)}
                </strong>
              </div>

              <div className="agenda-card-main">
                <div className="agenda-card-tags">
                  <span>
                    {
                      CATEGORY_LABELS[
                        item.categoria
                      ] || item.categoria
                    }
                  </span>

                  <span>
                    {
                      LOCATION_LABELS[
                        item.tipo_local
                      ] || item.tipo_local
                    }
                  </span>

                  {item.modalidade ===
                    "individual" && (
                    <span>
                      Individual
                    </span>
                  )}

                  {!item.publicado && (
                    <span className="agenda-tag-hidden">
                      Privada
                    </span>
                  )}
                </div>

                <h2>
                  {item.titulo}
                </h2>

                {item.programa && (
                  <div className="agenda-program">
                    {item.programa}
                  </div>
                )}

                <div className="agenda-meta">
                  <span>
                    <Clock3 />
                    {timeRange(item)}
                  </span>

                  {item.tipo_local === "hub" && (
                    <span>
                      <MapPin />
                      {item.espaco_hub === "outro"
                        ? (
                            item.espaco_hub_outro ||
                            "Outro espaço"
                          )
                        : (
                            HUB_SPACE_LABELS[
                              item.espaco_hub
                            ] ||
                            "Espaço a definir"
                          )
                      }
                    </span>
                  )}

                  {item.tipo_local === "externa" &&
                    item.local && (
                    <span>
                      <MapPin />
                      {[
                        item.local,
                        item.municipio,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </span>
                  )}
                </div>

                {item.responsavel && (
                  <p>
                    <strong>Responsável:</strong>{" "}
                    {item.responsavel}
                  </p>
                )}

                {item.descricao && (
                  <p>
                    {item.descricao}
                  </p>
                )}

                <small>
                  Status:{" "}
                  {
                    STATUS_LABELS[
                      item.status
                    ] || item.status
                  }
                </small>
              </div>

              <div className="agenda-card-actions">
                <button
                  title={
                    item.publicado
                      ? "Tornar privada"
                      : "Tornar pública"
                  }
                  onClick={() =>
                    togglePublished(item)
                  }
                >
                  {item.publicado
                    ? <Eye />
                    : <EyeOff />
                  }
                </button>

                <button
                  title="Editar"
                  onClick={() =>
                    openEdit(item)
                  }
                >
                  <Edit3 />
                </button>

                <button
                  className="agenda-delete"
                  title="Excluir"
                  onClick={() =>
                    remove(item)
                  }
                >
                  <Trash2 />
                </button>
              </div>
            </article>
          ))}

        </section>
      )}


      {formOpen && (
        <div className="agenda-modal-backdrop">

          <form
            className="agenda-modal"
            onSubmit={save}
          >
            <header>
              <div>
                <span>
                  {editing
                    ? "EDITAR ATIVIDADE"
                    : "NOVA ATIVIDADE"
                  }
                </span>

                <h2>
                  {editing
                    ? "Atualizar agenda"
                    : "Adicionar à agenda"
                  }
                </h2>
              </div>

              <button
                type="button"
                onClick={closeForm}
              >
                <X />
              </button>
            </header>


            <div className="agenda-form-body">

              <label className="agenda-full">
                Título *
                <input
                  value={form.titulo}
                  onChange={(event) =>
                    change(
                      "titulo",
                      event.target.value
                    )
                  }
                  required
                />
              </label>


              <div className="agenda-form-grid">

                <label>
                  Categoria *
                  <select
                    value={form.categoria}
                    onChange={(event) =>
                      change(
                        "categoria",
                        event.target.value
                      )
                    }
                  >
                    {Object.entries(
                      CATEGORY_LABELS
                    ).map(
                      ([value, label]) => (
                        <option
                          value={value}
                          key={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </label>


                <label>
                  Tipo de atividade *
                  <select
                    value={form.tipo_local}
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setForm((current) => ({
                        ...current,
                        tipo_local: value,

                        ...(value === "hub"
                          ? {
                              local: "",
                              municipio: "",
                              espaco_hub:
                                current.espaco_hub ||
                                "a_definir",
                            }
                          : {
                              espaco_hub: "",
                              espaco_hub_outro: "",
                            }),
                      }));
                    }}
                  >
                    <option value="hub">
                      Interna no HUB
                    </option>

                    <option value="externa">
                      Externa com apoio do HUB
                    </option>
                  </select>
                </label>


                <label>
                  Modalidade
                  <select
                    value={form.modalidade}
                    onChange={(event) =>
                      change(
                        "modalidade",
                        event.target.value
                      )
                    }
                  >
                    <option value="coletiva">
                      Coletiva
                    </option>

                    <option value="individual">
                      Individual
                    </option>
                  </select>
                </label>


                <label>
                  Status
                  <select
                    value={form.status}
                    onChange={(event) =>
                      change(
                        "status",
                        event.target.value
                      )
                    }
                  >
                    {Object.entries(
                      STATUS_LABELS
                    ).map(
                      ([value, label]) => (
                        <option
                          value={value}
                          key={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </label>


                <label>
                  Data inicial *
                  <input
                    type="date"
                    value={form.data_inicio}
                    onChange={(event) =>
                      change(
                        "data_inicio",
                        event.target.value
                      )
                    }
                    required
                  />
                </label>


                <label>
                  Data final
                  <input
                    type="date"
                    value={form.data_fim}
                    onChange={(event) =>
                      change(
                        "data_fim",
                        event.target.value
                      )
                    }
                  />
                </label>


                <label>
                  Hora inicial
                  <input
                    type="time"
                    value={form.hora_inicio}
                    disabled={
                      form.horario_a_definir
                    }
                    onChange={(event) =>
                      change(
                        "hora_inicio",
                        event.target.value
                      )
                    }
                  />
                </label>


                <label>
                  Hora final
                  <input
                    type="time"
                    value={form.hora_fim}
                    disabled={
                      form.horario_a_definir
                    }
                    onChange={(event) =>
                      change(
                        "hora_fim",
                        event.target.value
                      )
                    }
                  />
                </label>

              </div>


              <label className="agenda-check">
                <input
                  type="checkbox"
                  checked={
                    form.horario_a_definir
                  }
                  onChange={(event) =>
                    change(
                      "horario_a_definir",
                      event.target.checked
                    )
                  }
                />

                Horário a definir
              </label>


              <div className="agenda-form-grid">

                {form.tipo_local === "hub" && (
                  <label>
                    Espaço do HUB *
                    <select
                      value={form.espaco_hub}
                      onChange={(event) =>
                        change(
                          "espaco_hub",
                          event.target.value
                        )
                      }
                      required
                    >
                      {Object.entries(
                        HUB_SPACE_LABELS
                      ).map(
                        ([value, label]) => (
                          <option
                            value={value}
                            key={value}
                          >
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </label>
                )}


                {form.tipo_local === "hub" &&
                  form.espaco_hub === "outro" && (
                  <label>
                    Informe o espaço *
                    <input
                      value={
                        form.espaco_hub_outro
                      }
                      onChange={(event) =>
                        change(
                          "espaco_hub_outro",
                          event.target.value
                        )
                      }
                      required
                    />
                  </label>
                )}


                {form.tipo_local === "externa" && (
                  <label>
                    Local da atividade *
                    <input
                      value={form.local}
                      onChange={(event) =>
                        change(
                          "local",
                          event.target.value
                        )
                      }
                      required
                    />
                  </label>
                )}


                {form.tipo_local === "externa" && (
                  <label>
                    Município
                    <input
                      value={form.municipio}
                      onChange={(event) =>
                        change(
                          "municipio",
                          event.target.value
                        )
                      }
                    />
                  </label>
                )}


                <label>
                  Responsável / representante
                  <input
                    value={form.responsavel}
                    onChange={(event) =>
                      change(
                        "responsavel",
                        event.target.value
                      )
                    }
                  />
                </label>


                <label>
                  Projeto / programa
                  <input
                    value={form.programa}
                    onChange={(event) =>
                      change(
                        "programa",
                        event.target.value
                      )
                    }
                  />
                </label>

              </div>


              <label className="agenda-full">
                Descrição
                <textarea
                  rows="4"
                  value={form.descricao}
                  onChange={(event) =>
                    change(
                      "descricao",
                      event.target.value
                    )
                  }
                />
              </label>


              <div className="agenda-form-grid">
                <label>
                  Visibilidade *
                  <select
                    value={
                      form.publicado
                        ? "publica"
                        : "privada"
                    }
                    onChange={(event) =>
                      change(
                        "publicado",
                        event.target.value ===
                          "publica"
                      )
                    }
                  >
                    <option value="publica">
                      Pública
                    </option>

                    <option value="privada">
                      Privada
                    </option>
                  </select>
                </label>
              </div>

            </div>


            <footer>
              <button
                type="button"
                className="admin-secondary-button"
                onClick={closeForm}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="admin-primary-button"
                disabled={saving}
              >
                {saving
                  ? "Salvando..."
                  : "Salvar atividade"
                }
              </button>
            </footer>

          </form>

        </div>
      )}

    </div>
  );
}

export default AgendaAdmin;
